import * as DialogPrimitive from '@radix-ui/react-dialog';

import React, { forwardRef, useEffect } from 'react';

import { DialogCloseButton } from '@/components/dialog-close';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

import { cn } from '@/lib/utils';

type DialogContentPropsWithOverlay = React.ComponentPropsWithoutRef<
	typeof DialogPrimitive.Content
> & {
	overlayProps?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;
};

interface Props extends React.PropsWithChildren {
	titleContent?: string | React.ReactNode;
	descriptionContent?: string | React.ReactNode;
	footerContent?: React.ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	contentProps?: DialogContentPropsWithOverlay;
	contentClassName?: string;
	viewportClassName?: string;
	triggerContent?: React.ReactNode;
	footerClassName?: string;
	forceDisplayBlockForContent?: boolean;
}

const Modal = forwardRef<HTMLDivElement, Props>(
	(
		{
			children,
			titleContent,
			descriptionContent,
			footerContent,
			open,
			onOpenChange,
			contentProps = {},
			contentClassName = '',
			viewportClassName,
			triggerContent,
			footerClassName = '',
			forceDisplayBlockForContent = false,
		},
		ref
	) => {
		const handleEscapeKeyDown = (e: KeyboardEvent) => {
			// Prevent escape key from closing the modal if the target is a textarea or input
			const target = e.target as HTMLElement;
			const targetTag = target?.tagName?.toLowerCase?.() || '';
			const isContentEditable = target?.getAttribute('contenteditable') === 'true';
			if (targetTag === 'textarea' || targetTag === 'input' || isContentEditable) {
				e.preventDefault();
				target?.blur();
			}
		};

		useEffect(() => {
			if (!open)
				setTimeout(() => {
					document.body.style.pointerEvents = 'auto';
				}, 700);
		}, [open]);

		useEffect(() => {
			return () => {
				setTimeout(() => {
					document.body.style.pointerEvents = 'auto';
				}, 400);
			};
		}, []);

		const footer = <DialogFooter className={cn(footerClassName)}>{footerContent}</DialogFooter>;

		return (
			<Dialog
				open={open}
				onOpenChange={onOpenChange}
			>
				{triggerContent ? <DialogTrigger asChild>{triggerContent}</DialogTrigger> : null}
				<DialogContent
					className={cn('block max-h-full overflow-hidden p-0', contentClassName)}
					overlayProps={{ className: 'inset-[-2px]', ...(contentProps.overlayProps || {}) }}
					{...contentProps}
					onEscapeKeyDown={contentProps.onEscapeKeyDown || handleEscapeKeyDown}
				>
					<div
						ref={ref}
						className={cn('flex h-fit max-h-[88vh] flex-col')}
					>
						{(titleContent || descriptionContent) && (
							<div className="p-6 pb-0">
								{titleContent && (
									<DialogHeader className={cn(descriptionContent ? '' : 'mb-3')}>
										<DialogTitle className="min-w-0 flex-1">{titleContent}</DialogTitle>
										<DialogCloseButton className="absolute right-3 top-3 z-10 flex size-7 items-center justify-center rounded-md text-muted-foreground text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50" />
									</DialogHeader>
								)}
								{descriptionContent && (
									<DialogDescription className={'my-4'}>{descriptionContent}</DialogDescription>
								)}
							</div>
						)}

						<div className="flex min-h-0 flex-1 overflow-hidden">
							<ScrollArea
								className="flex h-[inherit] w-full"
								viewportClassName={cn(
									'p-6',
									titleContent && 'pt-0',
									forceDisplayBlockForContent && '[&>div]:!block',
									viewportClassName
								)}
							>
								{children}
							</ScrollArea>
						</div>

						{footerContent && <div className="p-6 pt-0">{footer}</div>}
					</div>
				</DialogContent>
			</Dialog>
		);
	}
);

Modal.displayName = 'Modal';

export default Modal;
